<!-- C:\platforma-frontend-admin-creator\app-subdomain-creators\src\accountPages\CreatorProfileSettings.vue -->

<template>
  <div class="flex min-h-full flex-1 flex-col justify-center px-4 py-6 sm:px-6 lg:px-8">
    <div class="mx-auto w-full max-w-3xl">
      <h2 class="text-center text-2xl font-bold leading-9 tracking-tight text-zinc-800">
        {{ isStaffProfile ? 'Staff Profile Settings' : 'Creator Profile Settings' }}
      </h2>
      <p class="mt-2 text-center text-sm text-zinc-500">
        {{ isStaffProfile ? 'Update your staff account identity details.' : 'Update your creator account identity details.' }}
      </p>

      <div class="mt-8 grid grid-cols-1 gap-y-8">
        <div class="col-span-full flex items-center gap-x-8">
          <div class="relative">
            <!-- Skeleton Loader -->
            <div
              v-if="isLoading"
              class="h-24 w-24 animate-pulse rounded-full bg-zinc-300 shadow-lg"
            />

            <!-- Profile Image -->
            <img
              v-else
              :src="profilePicSrc"
              class="h-24 w-24 flex-none rounded-full object-cover shadow-lg"
              alt="Profile Picture"
              @load="handleImageLoad"
            >
          </div>
          <div>
            <input
              id="fileInput"
              type="file"
              accept="image/jpeg, image/png, image/gif"
              style="display: none"
              @change="uploadProfilePicture"
            >
            <button
              type="button"
              class="rounded-md border border-gray-300 bg-white/10 px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm hover:bg-white/20"
              @click="triggerFileInput"
            >
              Change avatar
            </button>
            <p class="mt-2 text-xs leading-5 text-gray-400">
              JPG, GIF, or PNG. 1MB max.
            </p>
          </div>
        </div>

        <!-- Success Notification -->
        <div
          v-if="showSuccess"
          class="flex items-center gap-2 rounded-md border border-green-400 bg-green-100 p-3"
        >
          <svg
            class="h-5 w-5 text-green-500"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M5 13l4 4L19 7"
            />
          </svg>
          <span class="text-sm text-green-700">Profile picture updated successfully!</span>
        </div>

        <form
          class="grid grid-cols-1 gap-x-5 gap-y-6 sm:grid-cols-2"
          @submit.prevent="saveProfileDetails"
        >
          <div class="sm:col-span-1">
            <label
              for="creator-first-name"
              class="block text-sm font-medium leading-6 text-zinc-600"
            >First name</label>
            <div class="mt-1">
              <input
                id="creator-first-name"
                v-model="profileForm.firstName"
                type="text"
                autocomplete="given-name"
                class="block w-full rounded-md border-0 bg-white/10 py-1.5 text-zinc-700 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-[#2D8CFF] sm:text-sm sm:leading-6"
                :disabled="detailsLoading || detailsSaving"
              >
            </div>
          </div>

          <div class="sm:col-span-1">
            <label
              for="creator-last-name"
              class="block text-sm font-medium leading-6 text-zinc-600"
            >Last name</label>
            <div class="mt-1">
              <input
                id="creator-last-name"
                v-model="profileForm.lastName"
                type="text"
                autocomplete="family-name"
                class="block w-full rounded-md border-0 bg-white/10 py-1.5 text-zinc-700 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-[#2D8CFF] sm:text-sm sm:leading-6"
                :disabled="detailsLoading || detailsSaving"
              >
            </div>
          </div>

          <div class="col-span-full">
            <label
              for="creator-email"
              class="block text-sm font-medium leading-6 text-zinc-600"
            >Email address</label>
            <div class="mt-1">
              <input
                id="creator-email"
                v-model="profileForm.email"
                type="email"
                autocomplete="email"
                class="block w-full rounded-md border-0 bg-white/10 py-1.5 text-zinc-700 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-[#2D8CFF] sm:text-sm sm:leading-6"
                :disabled="detailsLoading || detailsSaving"
              >
            </div>
          </div>

          <div class="col-span-full flex flex-wrap items-center gap-2 pt-1">
            <button
              type="submit"
              class="rounded-md px-4 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
              :class="disableProfileSave
                ? 'cursor-not-allowed bg-gray-300'
                : 'bg-[#2D8CFF] hover:bg-[#2577d9] focus-visible:outline-[#2D8CFF]'"
              :disabled="disableProfileSave"
            >
              {{ detailsSaving ? 'Saving...' : 'Save' }}
            </button>
            <button
              v-if="hasProfileChanges"
              type="button"
              class="rounded-md bg-zinc-300 px-4 py-2 text-sm font-semibold text-zinc-700 shadow-sm hover:bg-zinc-400"
              :disabled="detailsSaving"
              @click="cancelProfileEdits"
            >
              Cancel
            </button>
          </div>
        </form>

        <div class="flex flex-wrap items-center gap-3 border-t border-zinc-200 pt-4">
          <button
            type="button"
            class="inline-flex items-center rounded-md bg-zinc-900 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-zinc-700 disabled:cursor-not-allowed disabled:opacity-60"
            :disabled="passwordResetSending || !initialProfileForm.email"
            @click="sendCreatorPasswordResetEmail"
          >
            {{ passwordResetSending ? 'Sending...' : 'Send Password Reset Email' }}
          </button>
          <p
            v-if="passwordResetSuccess"
            class="text-sm text-green-700"
          >
            {{ passwordResetSuccess }}
          </p>
          <p
            v-if="passwordResetError"
            class="text-sm text-red-600"
          >
            {{ passwordResetError }}
          </p>
        </div>

        <div
          v-if="detailsError"
          class="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600"
        >
          {{ detailsError }}
        </div>
        <div
          v-if="detailsSuccess"
          class="rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700"
        >
          {{ detailsSuccess }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { fetchCreatorStaffSession } from '@/utils/creatorStaffAccess';

const defaultAvatarSrc = new URL('@/assets/media/images/default-avatar.png', import.meta.url).href;
const profilePicSrc = ref('');
const isLoading = ref(true);
const showSuccess = ref(false);
const isStaffProfile = ref(false);
const detailsLoading = ref(false);
const detailsSaving = ref(false);
const detailsError = ref('');
const detailsSuccess = ref('');
const passwordResetSending = ref(false);
const passwordResetSuccess = ref('');
const passwordResetError = ref('');
const profileForm = reactive({
  firstName: '',
  lastName: '',
  email: '',
});
const initialProfileForm = ref({
  firstName: '',
  lastName: '',
  email: '',
});

const apiProxyUrl = process.env.VUE_APP_API_PROXY_URL;
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const normalizeText = (value) => String(value == null ? '' : value).trim();
const normalizeEmail = (value) => normalizeText(value).toLowerCase();
const isValidEmail = (value) => emailRegex.test(normalizeEmail(value));

const hasProfileChanges = computed(() => (
  normalizeText(profileForm.firstName) !== initialProfileForm.value.firstName
  || normalizeText(profileForm.lastName) !== initialProfileForm.value.lastName
  || normalizeEmail(profileForm.email) !== initialProfileForm.value.email
));

const disableProfileSave = computed(() => (
  detailsLoading.value
  || detailsSaving.value
  || !hasProfileChanges.value
  || !normalizeText(profileForm.firstName)
  || !isValidEmail(profileForm.email)
));

const syncInitialProfileForm = () => {
  initialProfileForm.value = {
    firstName: normalizeText(profileForm.firstName),
    lastName: normalizeText(profileForm.lastName),
    email: normalizeEmail(profileForm.email),
  };
};

const applyCreatorProfilePayload = (payload = {}) => {
  profileForm.firstName = normalizeText(payload?.firstName);
  profileForm.lastName = normalizeText(payload?.lastName);
  profileForm.email = normalizeText(payload?.email);
  syncInitialProfileForm();
};

// Fetch the profile picture URL
const fetchProfilePicture = async () => {
  isLoading.value = true;
  await new Promise(resolve => setTimeout(resolve, 500)); // Ensure skeleton loader is visible

  try {
    const imageType = 'creator_profile_pic_main';
    const response = await fetch(`${apiProxyUrl}/api/uploads/get-image?imageType=${encodeURIComponent(imageType)}`, {
      method: 'GET',
      credentials: 'include',
    });

    if (!response.ok) throw new Error('Failed to fetch profile picture.');

    const { fileUrl } = await response.json();
    profilePicSrc.value = fileUrl || defaultAvatarSrc;
  } catch (error) {
    console.error('Error fetching profile picture:', error);
    profilePicSrc.value = defaultAvatarSrc;
  } finally {
    isLoading.value = false;
  }
};

const fetchCreatorDetails = async () => {
  detailsLoading.value = true;
  detailsError.value = '';

  try {
    const response = await fetch(`${apiProxyUrl}/api/creator-details`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(String(payload?.message || payload?.error || 'Failed to load account profile details.'));
    }

    applyCreatorProfilePayload(payload || {});
  } catch (error) {
    console.error('Error fetching creator details:', error);
    detailsError.value = String(error?.message || 'Unable to load account profile details right now.');
  } finally {
    detailsLoading.value = false;
  }
};

const saveProfileDetails = async () => {
  detailsError.value = '';
  detailsSuccess.value = '';
  passwordResetSuccess.value = '';
  passwordResetError.value = '';

  if (!normalizeText(profileForm.firstName)) {
    detailsError.value = 'First name is required.';
    return;
  }

  if (!isValidEmail(profileForm.email)) {
    detailsError.value = 'Please enter a valid email address.';
    return;
  }

  if (!hasProfileChanges.value) {
    return;
  }

  detailsSaving.value = true;
  try {
    const response = await fetch(`${apiProxyUrl}/api/creator-details`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        firstName: normalizeText(profileForm.firstName),
        lastName: normalizeText(profileForm.lastName),
        email: normalizeEmail(profileForm.email),
      }),
    });

    const payload = await response.json().catch(() => ({}));
    if (!response.ok || payload?.success === false) {
      throw new Error(String(payload?.message || payload?.error || 'Failed to update profile details.'));
    }

    applyCreatorProfilePayload(payload || {});
    detailsSuccess.value = String(payload?.message || 'Profile details updated successfully.');
  } catch (error) {
    console.error('Error saving creator details:', error);
    detailsError.value = String(error?.message || 'Unable to save profile details right now.');
  } finally {
    detailsSaving.value = false;
  }
};

const cancelProfileEdits = () => {
  profileForm.firstName = initialProfileForm.value.firstName;
  profileForm.lastName = initialProfileForm.value.lastName;
  profileForm.email = initialProfileForm.value.email;
  detailsError.value = '';
  detailsSuccess.value = '';
};

const sendCreatorPasswordResetEmail = async () => {
  passwordResetSuccess.value = '';
  passwordResetError.value = '';
  const email = normalizeEmail(initialProfileForm.value.email || profileForm.email);

  if (!isValidEmail(email)) {
    passwordResetError.value = 'A valid account email is required before sending reset email.';
    return;
  }

  passwordResetSending.value = true;
  try {
    const response = await fetch(`${apiProxyUrl}/api/auth/password-recovery/request`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || payload?.success === false) {
      throw new Error(String(payload?.message || payload?.error || 'Failed to send password reset email.'));
    }
    passwordResetSuccess.value = String(payload?.message || 'Password reset email request submitted.');
  } catch (error) {
    console.error('Error requesting creator password reset email:', error);
    passwordResetError.value = String(error?.message || 'Unable to send password reset email right now.');
  } finally {
    passwordResetSending.value = false;
  }
};

// Upload a new profile picture
const uploadProfilePicture = async (event) => {
  const file = event.target.files[0];
  if (!file) return;

  isLoading.value = true;
  try {
    const sanitizedFilename = encodeURIComponent(file.name.replace(/[^a-zA-Z0-9._-]/g, '_'));
    const imageType = 'creator_profile_pic_main';

    const response = await fetch(`${apiProxyUrl}/api/uploads/generate-signed-url`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ filename: sanitizedFilename, contentType: file.type, imageType }),
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Error fetching signed URL:', errorData);
      throw new Error(errorData.message || 'Failed to fetch signed URL');
    }

    const { signedUrl, permanentUrl } = await response.json();

    const uploadResponse = await fetch(signedUrl, {
      method: 'PUT',
      headers: { 'Content-Type': file.type },
      body: file,
    });

    if (!uploadResponse.ok) {
      console.error('Error uploading image:', await uploadResponse.text());
      throw new Error('Error uploading image to the signed URL.');
    }

    profilePicSrc.value = permanentUrl;
    showSuccess.value = true; // Show success notification
    setTimeout(() => {
      location.reload(); // Reload page after delay
    }, 1500); // Wait to show the success message briefly
  } catch (error) {
    console.error('Error uploading profile picture:', error);
    alert('Error uploading profile picture. Please try again.');
  } finally {
    isLoading.value = false;
  }
};

const handleImageLoad = () => {
  isLoading.value = false;
};

// Trigger file input for selecting an image
const triggerFileInput = () => {
  document.getElementById('fileInput').click();
};

// Fetch profile picture on mount
onMounted(async () => {
  try {
    const payload = await fetchCreatorStaffSession({ force: true });
    isStaffProfile.value = Boolean(payload?.isStaff);
  } catch (_error) {
    // keep creator heading fallback
  }

  await Promise.all([
    fetchProfilePicture(),
    fetchCreatorDetails(),
  ]);
});
</script>






  
  
